/**
 * This module keeps track of the state for all discover/ route data
 * such as views and brand products, as well as discover-specific alerts.
 * All mutations are namespaced discover/.
 */
import { findIndex } from 'lodash';
import firebase from '../../services/firebase';
import utils from '../../../src/services/utils';

export const ALERT_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  SYNC: 'sync',
};

export const ARCHIVED_ITEM_TYPES = {
  PRODUCT: 'Product',
  VIEW: 'Experience',
};

const findIdxAndMergeUpdates = (arr, id, type, updates) => {
  const idx = findIndex(arr, (item) => item.id === id && item.type === type);
  const updatedItem = Object.assign({}, arr[idx], updates);

  return { idx, updatedItem };
};

const sortByArchived = (arr, type) => {
  const archived = [];
  const active = [];

  arr.forEach(item => {
    const updatedWithType = { ...item, type };

    if (item.archived) {
      archived.push(updatedWithType);
    } else {
      active.push(updatedWithType);
    }
  });

  return { archived, active };
};

const removeItemFromArr = (arr, idx) => [...arr.slice(0, idx), ...arr.slice(idx + 1)];

const state = {
  views: null,
  brandProductFeed: null,
  alert: {},
  archived: [],
  activeViewSlideOut: null,
  selectedView: {
    products: [],
  },
  cartSlideUp: {
    show: false,
    isExpanded: false,
    view: {},
  },
  viewSlideOutNotificationType: null,
};

const mutations = {
  setAlert(state, { type, message }) {
    state.alert = {
      type: type,
      message: message,
    };

    if (type === ALERT_TYPES.SUCCESS) {
      setTimeout(() => {
        state.alert = {};
      }, 4000);
    }

  },
  clearAlert(state) {
    state.alert = {};
  },
  setViews(state, views) {
    state.views = views;
  },
  setBrandProductFeed(state, brandProducts) {
    state.brandProductFeed = brandProducts;
  },
  setArchivedItems(state, items) {
    state.archived = items;
  },
  addView(state, newView) {
    newView.type = ARCHIVED_ITEM_TYPES.VIEW;

    if (state.views && state.views.length) {
      state.views = [newView, ...state.views];
    } else {
      state.views = [newView];
    }
  },
  addProduct(state, newProduct) {
    newProduct.type = ARCHIVED_ITEM_TYPES.PRODUCT;

    if (state.brandProductFeed && state.brandProductFeed.length) {
      state.brandProductFeed = [newProduct, ...state.brandProductFeed];
    } else {
      state.brandProductFeed = [newProduct];
    }
  },
  addArchivedItem(state, newArchivedItem) {
    state.archived = [...state.archived, newArchivedItem];
  },
  updateView(state, { id, updatedView }) {
    const idx = findIndex(state.views, (v) => v.id === id);
    const views = Object.assign([], state.views);

    views[idx] = {
      ...state.views[idx],
      ...updatedView,
    };
    state.views = views;
  },
  updateProductFeed(state, updatedProduct, productIdx) {
    const idx = productIdx ||  findIndex(state.brandProductFeed, (p) => p.id === updatedProduct.id);
    const brandProductFeed = Object.assign([], state.brandProductFeed);

    brandProductFeed[idx] = {
      ...state.brandProductFeed[idx],
      ...updatedProduct,
    };
    state.brandProductFeed = brandProductFeed;
  },
  removeArchivedItem(state, idx) {
    state.archived = removeItemFromArr(state.archived, idx);
  },
  removeView(state, idx) {
    state.views = removeItemFromArr(state.views, idx);
  },
  removeProduct(state, idx) {
    state.brandProductFeed = removeItemFromArr(state.brandProductFeed, idx);
  },
  updateCartSlideUp(state, cartDetails) {
    state.cartSlideUp = Object.assign(state.cartSlideUp, cartDetails);
  },
  updateActiveViewSlideOut(state, slideOutType) {
    state.activeViewSlideOut = slideOutType;
  },
  updateSelectedView(state, view) {
    state.selectedView = view;
  },
  showViewSlideOutNotification(state, notificationType) {
    state.viewSlideOutNotificationType = notificationType;
  },
};

const actions = {
  showAlert({ commit }, alertPayload) {
    commit('setAlert', alertPayload);
  },
  updateOrAddToProductFeed({ commit }, updatedProduct) {
    const idx = findIndex(state.brandProductFeed, (p) => p.id === updatedProduct.id);

    if (idx === -1) {
      commit('addProduct', updatedProduct);

      return;
    }

    commit('updateProductFeed', updatedProduct, idx);
  },
  async fetchAndSortViews({ commit, dispatch, rootState }, { brandId, token }) {
    const currentBrandId = brandId || rootState.attachStudio.actingBrandId;

    try {
      const allViews = await firebase.getDiscoverViewsByBrandId(currentBrandId, token);
      const { active, archived } = sortByArchived(allViews, ARCHIVED_ITEM_TYPES.VIEW);

      commit('setViews', active);
      archived.forEach(item => commit('addArchivedItem', item));
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async fetchAndSortBrandProductFeed({ commit, dispatch, rootState }, { brandId, token }) {
    const currentBrandId = brandId || rootState.attachStudio.actingBrandId;

    try {
      const allBrandProducts = await firebase.getDiscoverProductsByBrandId(currentBrandId, token);
      const { active, archived } = sortByArchived(allBrandProducts, ARCHIVED_ITEM_TYPES.PRODUCT);

      commit('setBrandProductFeed', active);
      archived.forEach(item => commit('addArchivedItem', item));
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async createNewView({ commit, dispatch }, { newView, token }) {
    try {
      await firebase.setDiscoverView(newView, token);
      commit('addView', newView);
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async updateView({ commit, dispatch }, { id, updatedView, token }) {
    try {
      await firebase.updateDiscoverView(id, updatedView, token);
      commit('updateView', { id, updatedView });
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async updateProduct({ commit, dispatch }, { updatedProduct, token }) {
    try {
      await firebase.updateProductFeed(updatedProduct.id, updatedProduct, token);
      commit('updateProductFeed', updatedProduct);
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async markAsArchived({ commit, dispatch, state }, { id, type, token }) {
    const payload = { archived: true, archivedAt: new Date().toISOString() };
    const activeItems = type === ARCHIVED_ITEM_TYPES.PRODUCT ? state.brandProductFeed : state.views;
    const { idx, updatedItem: archivedItem } = findIdxAndMergeUpdates(
      activeItems, id, type, payload,
    );

    try {
      if (type === ARCHIVED_ITEM_TYPES.PRODUCT) {
        await firebase.updateProductFeed(id, payload, token);
        commit('removeProduct', idx);
      } else {
        await firebase.updateDiscoverView(id, payload, token);
        commit('removeView', idx);
      }

      commit('addArchivedItem', archivedItem);
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async restoreArchivedItem({ commit, dispatch, state }, { id, type, skipFirebase, token }) {
    const payload = { archived: false, archivedAt: '' };
    const { idx, updatedItem } = findIdxAndMergeUpdates(state.archived, id, type, payload);

    try {
      if (type === ARCHIVED_ITEM_TYPES.PRODUCT) {
        if (!skipFirebase) { await firebase.updateProductFeed(id, payload, token); }
        commit('addProduct', updatedItem);
      } else {
        if (!skipFirebase) { await firebase.updateDiscoverView(id, payload, token); }
        commit('addView', updatedItem);
      }
      commit('removeArchivedItem', idx);
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async fetchVIPDetails({ dispatch }, { brandId, search }) {
    try {
      return utils.postCallVIPService('vip/products/query', { brandId, search });
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async fetchVIPAssociation({ dispatch }, { mikmakProductId, brandId }) {
    try {
      return utils.postCallVIPService(`vip/products/query/association`,
        { mikmakProductId, brandId },
      );
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async upsertVIPAssociation({ dispatch }, { mikmakProductId, productIds, brandId }) {
    try {
      return utils.postCallVIPService('vip/products/associate',
        { mikmakProductId, productIds, brandId },
      );
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
  async fetchHasVIPData({ dispatch }, { brandId }) {
    try {
      return utils.postCallVIPService('vip/products/is-vip', { brandId });
    } catch (err) {
      dispatch('showAlert', { type: ALERT_TYPES.ERROR, message: err });
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
